import { InjectionToken } from '@angular/core';
import type { Signal } from '@angular/core';
import type { Observable } from 'rxjs';
import type { AppliedFilters, FilterResponse, Counterpart } from './filter.model';

/**
 * IFilterStore
 *
 * Public contract for any filter store implementation.
 * Components inject this token instead of a concrete class, which means:
 *  - Any feature (Transactions, Payments, Reports…) can provide its own
 *    store under this token without touching shared components.
 *  - Unit tests swap in a minimal stub — no real HTTP calls.
 *
 * @example — providing the transaction implementation at route level:
 * ```ts
 * // transactions.routes.ts
 * providers: [
 *   { provide: FILTER_STORE_TOKEN, useExisting: TransactionFilterStore },
 * ]
 * ```
 *
 * @example — injecting in a component:
 * ```ts
 * protected readonly filterStore = inject(FILTER_STORE_TOKEN);
 * ```
 */
export interface IFilterStore {
  // ── Reactive state ─────────────────────────────────────────────────────────

  /** Available filter options loaded from the backend (null before first load). */
  readonly availableFilters: Signal<FilterResponse | null>;

  /** Currently applied filter state — source of truth for the whole filter UI. */
  readonly appliedFilters: Signal<AppliedFilters>;

  /** Counterparts (clients) list — lazy-loaded on first panel open. */
  readonly counterparts: Signal<Counterpart[]>;

  /** True while filter options are being fetched from the API. */
  readonly loading: Signal<boolean>;

  // ── Derived state ───────────────────────────────────────────────────────────

  /** Total number of active filter categories (each key counts as 1). */
  readonly activeFilterCount: Signal<number>;

  /**
   * True when any filter is active.
   * Drives "Effacer les filtres" visibility and the inline chip row.
   */
  readonly hasActiveFilters: Signal<boolean>;

  /**
   * True when at least one "advanced" filter is active
   * (transactionTypes, categoryIds, counterpartIds, accountIds).
   * Drives Row 2 chip strip visibility.
   */
  readonly hasAdvancedFilters: Signal<boolean>;

  // ── Event stream ────────────────────────────────────────────────────────────

  /**
   * Fires only when the user explicitly applies, clears or resets filters.
   * Consumers (e.g. TransactionListComponent) subscribe here to reload data.
   * Distinct from `appliedFilters` signal to avoid skip(1) workarounds.
   */
  readonly filterApplied$: Observable<AppliedFilters>;

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Merges a partial filter update into the current state, syncs the URL,
   * and emits on filterApplied$.
   */
  applyFilters(filters: Partial<AppliedFilters>): void;

  /**
   * Removes a single filter key, syncs the URL, and emits on filterApplied$.
   */
  clearFilter(filterKey: keyof AppliedFilters): void;

  /**
   * Replaces the full filter state (no merge), syncs the URL, and emits on filterApplied$.
   * Use this when removing chips to avoid stale keys surviving via merge semantics.
   */
  replaceFilters(filters: AppliedFilters): void;

  /**
   * Resets all filters to empty state, clears URL params, and emits on filterApplied$.
   */
  clearAllFilters(): void;

  // ── Data loading ────────────────────────────────────────────────────────────

  /**
   * Fetches available filter options from the backend API.
   * Should be called once on feature initialisation.
   */
  loadFilterOptions(): void;

  /**
   * Lazy-loads counterparts (clients) from the API.
   * Cached after first successful load — safe to call multiple times.
   */
  loadCounterparts(): void;
}

/**
 * FILTER_STORE_TOKEN
 *
 * Injection token for the filter store contract.
 * Provide a concrete implementation at the feature route or component level.
 *
 * Using `useExisting` (not `useClass`) ensures Angular's DI resolves to the
 * same singleton instance rather than creating a second object.
 */
export const FILTER_STORE_TOKEN = new InjectionToken<IFilterStore>('FilterStore');
